import { mergeClassNames } from '../../../utils/mergeClassNames';
import {
  createContext,
  useMemo,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  ToggleButtonGroup,
  type ToggleButtonGroupProps,
} from 'react-aria-components';
import styles from './SegmentedControl.module.scss';

type SegmentedControlSize = 'compact' | 'small' | 'medium';

type SegmentedControlContextValue = {
  size: SegmentedControlSize;
};

export const BetaSegmentedControlContext =
  createContext<SegmentedControlContextValue>({
    size: 'small',
  });

export type BetaSegmentedControlProps = {
  /** The currently selected key (controlled). */
  selectedKey?: string;

  /** The default selected key (uncontrolled). */
  defaultSelectedKey?: string;

  /** Handler called when the selected key changes. */
  onSelectionChange?: (value: string) => void;

  /** The size variant of the segmented control. @default 'small' */
  size?: SegmentedControlSize;

  /** Whether the entire segmented control is disabled. */
  isDisabled?: boolean;

  /** An accessible label for the segmented control. */
  'aria-label'?: string;

  /** The id of the element that labels the segmented control. */
  'aria-labelledby'?: string;

  /** Identifies the element(s) that describe the segmented control. */
  'aria-describedby'?: string;

  /** Additional CSS class name. */
  className?: ToggleButtonGroupProps['className'];

  /** The inline style for the element. */
  style?: CSSProperties;

  /** BetaSegmentedControlOption children. */
  children: ReactNode;
};

/**
 * `BetaSegmentedControl` is a set of two or more segments, each acting as a
 * mutually exclusive option. It lets people switch views, filter or sort
 * content, or choose among related options.
 *
 * Each option is individually focusable via Tab. Press Enter or Space to
 * select the focused option.
 *
 * ```tsx
 * <BetaSegmentedControl defaultValue="list" aria-label="View mode">
 *   <BetaSegmentedControlOption value="list">List</BetaSegmentedControlOption>
 *   <BetaSegmentedControlOption value="grid">Grid</BetaSegmentedControlOption>
 * </BetaSegmentedControl>
 * ```
 */
export const BetaSegmentedControl = ({
  size = 'small',
  className,
  children,
  selectedKey: value,
  defaultSelectedKey: defaultValue,
  onSelectionChange: onChange,
  isDisabled = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  style,
}: BetaSegmentedControlProps) => {
  const segmentedControlContextValue = useMemo(() => ({ size }), [size]);
  const selectedKeys = useValueAsKeys(value);
  const defaultSelectedKeys = useValueAsKeys(defaultValue);

  return (
    <BetaSegmentedControlContext.Provider value={segmentedControlContextValue}>
      <ToggleButtonGroup
        className={(renderProps) =>
          mergeClassNames([styles.root, className], renderProps)
        }
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={selectedKeys}
        defaultSelectedKeys={defaultSelectedKeys}
        onSelectionChange={(keys) => {
          const [selected] = keys;
          if (selected != null) {
            onChange?.(String(selected));
          }
        }}
        isDisabled={isDisabled}
        data-size={size}
        data-disabled={isDisabled || undefined}
        data-zds
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        style={style}
      >
        {children}
      </ToggleButtonGroup>
    </BetaSegmentedControlContext.Provider>
  );
};

function useValueAsKeys(
  value: string | undefined | null
): Set<string> | undefined {
  const memoized = useMemo(() => {
    if (value == null) {
      return undefined;
    }
    return new Set([value]);
  }, [value]);

  return memoized;
}
